import { pluginDecorator } from '../plugin';

interface RenderOptions {
  plugins?: any;
}

const DATA_ATTR_NAMESPACE = 'axel';

export default function table(data: any, containerEl: HTMLElement, opts: RenderOptions = {}) {
  if (!containerEl) {
    throw new Error('Container element undefined');
  }
  if (!data) {
    throw new Error('Data undefined');
  }

  let buildTable = tableLayout;
  let buildHeader = tableHeaderLayout;
  let buildBody = tableBodyLayout;
  let buildRow = tableRowLayout;
  let buildCell = tableCellLayout;
  const decorator = pluginDecorator(containerEl, [
    'data',
    'buildHeader',
    'buildBody',
    'buildTable',
    'initialize',
    'beforeRender',
    'afterRender',
    'destroy',
  ]);

  const table: any = function table() {
    decorator.decorate(table, 'initialize');
    const tableData = decorator.decorate(data, 'data');
    const header = decorator.decorate(buildHeader.call(this, tableData.cols()), 'buildHeader');
    const body = decorator.decorate(buildBody.call(this, tableData, buildRow, buildCell), 'buildBody');
    const tableEl = decorator.decorate(buildTable.call(this, header, body), 'buildTable');

    decorator.decorate(tableEl, 'beforeRender');

    if (containerEl.hasChildNodes()) {
      containerEl.replaceChild(tableEl, containerEl.childNodes[0]);
    } else {
      containerEl.appendChild(tableEl);
    }

    decorator.decorate(tableEl, 'afterRender');
  };

  table.buildTable = function (_) {
    if (!arguments.length) {
      return buildTable;
    }
    buildTable = _;
    return table;
  };

  table.buildHeader = function (_) {
    if (!arguments.length) {
      return buildHeader;
    }
    buildHeader = _;
    return table;
  };

  table.buildBody = function (_) {
    if (!arguments.length) {
      return buildBody;
    }
    buildBody = _;
    return table;
  };

  table.buildRow = function (_) {
    if (!arguments.length) {
      return buildRow;
    }
    buildRow = _;
    return table;
  };

  table.buildCell = function (_) {
    if (!arguments.length) {
      return buildCell;
    }
    buildCell = _;
    return table;
  };

  table.decorator = function () {
    return decorator;
  };

  table.data = function () {
    return data;
  };

  table.destroy = function () {
    decorator.decorate(null, 'destroy');
  };

  table.plugin = decorator.registerer(table);

  return table;
}

function tableLayout(header, body) {
  const tableEl = document.createElement('table');

  tableEl.className = 'js-main-table main-table';
  tableEl.appendChild(header);
  tableEl.appendChild(body);
  return tableEl;
}

function tableHeaderLayout(columns = []) {
  const theadEl = document.createElement('thead');
  const insertCell = (rowEl, name, i) => {
    const colIndex = i + 1;
    const colClassName = [columnClassLayout(colIndex), 'js-col-header', 'col-header'].join(' ');
    const cellAttrs = {
      className: colClassName,
      data: {
        column: colIndex,
      },
    };
    rowEl.appendChild(tableCellLayout(name, cellAttrs, 'th'));
    return rowEl;
  };

  theadEl.appendChild(columns.reduce(insertCell, document.createElement('tr')));
  return theadEl;
}

function tableBodyLayout(data, rowLayout, cellLayout) {
  const rowBuilder = rowLayout || tableRowLayout;
  const rowKeys = data.rows();
  const insertRow = (bodyEl, rowKey, i) => {
    bodyEl.appendChild(rowBuilder(data.tree, rowKey, rowKeys.indexOf(rowKey), cellLayout));
    return bodyEl;
  };

  return rowKeys.reduce(insertRow, document.createElement('tbody'));
}

function tableRowLayout(tree, rowKey, i, cellLayout = tableCellLayout, selectedColIndex = null) {
  const rowIndex = i + 1;
  const rowEl = document.createElement('tr');
  const insertCell = (rowEl, colKey, j) => {
    const datum = tree[rowKey][colKey];
    const value = datum.format();
    const colIndex = j + 1;
    const className = [
      rowClassLayout(rowIndex),
      columnClassLayout(colIndex),
      cellTypeClassLayout(datum.type()),
      selectedColIndex == colIndex ? 'is-selected-col' : '',
    ].join(' ');
    const cellAttrs = {
      className: className,
      data: {
        row: rowIndex,
        rowKey: rowKey,
        column: colIndex,
      },
    };

    rowEl.appendChild(cellLayout(value, cellAttrs));
    return rowEl;
  };

  return Object.keys(tree[rowKey]).reduce(insertCell, rowEl);
}

function tableCellLayout(value, attrs, type = 'td') {
  const data = attrs.data;
  const cell = document.createElement(type);

  if (attrs.data) {
    for (const dataKey in attrs.data) {
      cell.setAttribute(['data', DATA_ATTR_NAMESPACE, dataKey].join('-'), attrs.data[dataKey]);
    }
  }
  cell.textContent = value;
  cell.className = attrs.className || '';
  return cell;
}

function columnClassLayout(int) {
  return 'col-' + int;
}

function rowClassLayout(int) {
  return 'row-' + int;
}

function cellTypeClassLayout(str) {
  return 'cell-type-' + str;
}
